import cx from 'classnames';
import { t } from 'i18next';
import { FC, useState, useRef, useEffect } from 'react';
import { usePushNotification } from '../../scripts/hooks/usePushNotification';
import { useIsMobile } from '../../scripts/hooks/useIsMobile';
import { NotificationControlPanel } from '../push/NotificationControlPanel';
import Modal from '../modal/modal';

type TNotificationBellProps = {
  className?: string;
};

export const NotificationBell: FC<TNotificationBellProps> = (props) => {
  const { isSupported, permissionState, subscription, isLoading } = usePushNotification();
  const [showDropdown, setShowDropdown] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const isMobile = useIsMobile();

  useEffect(() => {
    if (isMobile) {
      return;
    }

    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setShowDropdown(false);
      }
    };

    if (showDropdown) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showDropdown, isMobile]);

  if (!isSupported) {
    return null;
  }

  const isSubscribed = !!subscription;
  const isDenied = permissionState === 'denied';

  let iconClass: string;
  let iconStyle: string;
  let title: string;
  if (isDenied) {
    iconStyle = 'fa-regular';
    iconClass = 'fa-bell-slash text-muted';
    title = t('nav:notifications.blocked');
  } else if (isSubscribed) {
    iconStyle = 'fa-solid';
    iconClass = 'fa-bell';
    title = t('nav:notifications.enabled');
  } else {
    iconStyle = 'fa-regular';
    iconClass = 'fa-bell text-white';
    title = t('nav:notifications.enable');
  }

  const close = () => setShowDropdown(false);

  return (
    <div className={cx(props.className, 'rio-notification-bell')} ref={dropdownRef}>
      <button
        className="slds-button slds-button--icon rio-notification-bell__button"
        onClick={() => setShowDropdown(!showDropdown)}
        title={title}
        aria-label={title}
        disabled={isLoading}
      >
        {isLoading ? (
          <i className="fa fa-spinner fa-spin" />
        ) : (
          <i
            className={cx(iconStyle, 'fa-lg', iconClass, {
              'rio-notification-bell__icon--active': isSubscribed && !isDenied
            })}
          />
        )}
      </button>

      {/* Desktop: dropdown */}
      {showDropdown && !isMobile && (
        <div className="rio-notification-bell__dropdown slds-dropdown slds-dropdown--right">
          <NotificationControlPanel showSettingsLink allowDirectEnable onClose={close} compact />
        </div>
      )}

      {/* Mobile: modal */}
      {isMobile && (
        <Modal
          isOpen={showDropdown}
          onRequestClose={close}
          title={t('settings:push-notifications.title')}
          centered
        >
          <Modal.Body>
            <NotificationControlPanel showSettingsLink allowDirectEnable onClose={close} compact />
          </Modal.Body>
        </Modal>
      )}
    </div>
  );
};
